import React, { useEffect, useState } from "react";
import { Box, TextField, Button, Typography, MenuItem, Select, IconButton, FormControl, InputLabel } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

interface Widget {
  id: string;
  widgetType: string;
  config: Record<string, any>;
}

interface PropertiesPanelProps {
  widget: Widget;
  updateWidgetConfig: (id: string, newConfig: Record<string, any>) => void;
  onWidgetTypeChange?: (widgetId: string, newWidgetType: string) => void; 
  onClose: () => void;
}

const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  widget,
  updateWidgetConfig,
  onWidgetTypeChange,
  onClose
}) => {
  const [config, setConfig] = useState(widget.config);
  const [currentWidgetType, setCurrentWidgetType] = useState(widget.widgetType);

  console.log("Widget config in PropertiesPanel:", config);
  console.log("Current widget type:", currentWidgetType);

  // Available widget types
  const widgetTypes = [
    { value: "text-input", label: "Text Input" },
    { value: "textarea", label: "Text Area" },
    { value: "checkbox", label: "Checkbox" },
    { value: "radio-group", label: "Radio Group" },
    { value: "ranged-date", label: "Date Range" },
    { value: "date-picker", label: "Date Picker" },
    { value: "slider", label: "Slider" },
    { value: "dropdown", label: "Dropdown" },
    { value: "multiselect-dropdown", label: "Multi-Select Dropdown" },
    { value: "camera", label: "Camera" },
    { value: "location", label: "Location" },
  ];

  const handleChange = (key: string, value: any) => {
    setConfig((prevConfig) => ({
      ...prevConfig,
      [key]: value,
    }));
  };

  const handleWidgetTypeChange = (newWidgetType: string) => {
    if (newWidgetType !== currentWidgetType) {
      let newConfig = { ...config };
      if (["radio-group", "checkbox", "dropdown", "multiselect-dropdown"].includes(newWidgetType)) {
        if (!Array.isArray(newConfig.list)) {
          newConfig.list = [];
        }
      }
      setConfig(newConfig);
      setCurrentWidgetType(newWidgetType);
  
      if (onWidgetTypeChange) {
        onWidgetTypeChange(widget.id, newWidgetType);
      }
    }
  };
  

  const handleListChange = (key: string, index: number, newValue: string) => {
    const updatedList = [...(config[key] || [])];
    updatedList[index] = newValue;
    handleChange(key, updatedList);
  };

  const addListItem = (key: string) => {
    const updatedList = [...(config[key] || []), `Option ${config[key]?.length + 1 || 1}`];
    handleChange(key, updatedList);
  };

  const removeListItem = (key: string, index: number) => {
    const updatedList = config[key].filter((_: any, i: number) => i !== index);
    handleChange(key, updatedList);
  };

  const handleApply = () => {
    updateWidgetConfig(widget.id, config);
    onClose();
    console.log("Updated widget config:", config);
    console.log("Widget ID:", widget.id);
  };

  const fieldDisplayNames: Record<string, string> = {
    label: "Field Name",
    hintText: "Placeholder Text",
    editable: "Editable",
    required: "Mandatory Field",
    inputType: "Field Type",
    maxCharacters: "Maximum Characters Allowed",
    regexValidation: "Regex Validation",
  };

  const hiddenFields = new Set(["defaultText", "description", "value"]);

  const getRelevantFields = (widgetType: string, config: Record<string, any>) => {
    const commonFields = ["label", "required", "editable"];
    switch (widgetType) {
      case "text-input":
        return [...commonFields, "hintText", "inputType", "maxCharacters", "regexValidation"];
      case "textarea":
        return [...commonFields, "placeholder"];
      case "checkbox":
        return [...commonFields, "list", "checked"];
      case "radio-group":
        return [...commonFields, "list"];
      case "ranged-date":
        return [...commonFields, "difference", "startDate", "endDate"];
      case "date-picker":
        return [...commonFields, "defaultDate", "hint"];
      case "slider":
        return [...commonFields, "min", "max", "step"];
      case "dropdown":
        return [...commonFields, "hintText", "list", "defaultOption"];
      case "multiselect-dropdown":
        return [...commonFields, "hint", "list", "maxOptions"];
      case "camera":
        return [...commonFields, "gallery", "camera"];
      case "location":
        return [...commonFields, "display", "location", "lat", "lng"];
      default:
        return Object.keys(config).filter(key => !hiddenFields.has(key));
    }
  };

  const relevantFields = getRelevantFields(currentWidgetType, config);

  useEffect(() => {
    setConfig(widget.config);
    setCurrentWidgetType(widget.widgetType);
  }, [widget]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: 2,
        border: "1px solid #ddd",
        borderRadius: "4px",
        overflowY: "auto",
      }}
    >
      <Typography variant="h6" sx={{ marginBottom: 1 }}>
        Properties
      </Typography>

      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        {/* Widget Type Selector */}
        <Box sx={{ marginBottom: 2 }}>
          <Typography sx={{ fontWeight: 500, marginBottom: 1 }}>
            Widget Type
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={currentWidgetType}
              onChange={(e) => handleWidgetTypeChange(e.target.value)}
            >
              {widgetTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Dynamic Config Fields based on widget type */}
        {relevantFields.map((key) => {
          const value = config[key];
          return (
            <Box key={key} sx={{ marginBottom: 2 }}>
              <Typography
                sx={{
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={key}
              >
                {fieldDisplayNames[key] || key.charAt(0).toUpperCase() + key.slice(1)}
              </Typography>

              {typeof value === "object" && value?.type === "options" ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={value.selected || ""}
                    onChange={(e) => handleChange(key, { ...value, selected: e.target.value })}
                  >
                    {value.values.map((option: string, index: number) => (
                      <MenuItem key={index} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : Array.isArray(value) ? (
                <Box sx={{ marginTop: 1 }}>
                  {value.map((item, index) => (
                    <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center", marginBottom: 1 }}>
                      <TextField
                        value={item}
                        onChange={(e) => handleListChange(key, index, e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <IconButton onClick={() => removeListItem(key, index)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    fullWidth
                    onClick={() => addListItem(key)}
                  >
                    {key === "list" ? "Add Option" : "Add Item"}
                  </Button>
                </Box>
              ) : typeof value === "boolean" ? (
                <Select
                  value={value.toString()}
                  onChange={(e) => handleChange(key, e.target.value === "true")}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              ) : value === null || value === undefined ? (
                <Select
                  value={!!value ? "true" : "false"}
                  onChange={(e) => handleChange(key, e.target.value === "true")}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              ) : (
                <TextField
                  value={value}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    // For lat/lng (and other numeric fields), parse float or int as needed
                    if (["lat", "lng"].includes(key)) {
                      const num = parseFloat(inputValue);
                      handleChange(key, isNaN(num) ? inputValue : num); // allow text or number
                    } else if (
                      key === "maxCharacters" ||
                      key === "min" ||
                      key === "max" ||
                      key === "step" ||
                      key === "maxOptions" ||
                      (key === "value" && currentWidgetType === "slider")
                    ) {
                      const numValue = parseInt(inputValue, 10);
                      handleChange(key, isNaN(numValue) ? 0 : numValue);
                    } else {
                      handleChange(key, inputValue);
                    }
                  }}
                  fullWidth
                  size="small"
                  type={
                    key === "maxCharacters" ||
                      key === "min" ||
                      key === "max" ||
                      key === "step" ||
                      key === "maxOptions" ||
                      (key === "value" && currentWidgetType === "slider")
                      ? "number"
                      : key === "startDate" || key === "endDate" || key === "defaultDate"
                        ? "date"
                        : "text"
                  }
                />
              )}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ marginTop: 2 }}>
        <Button variant="contained" color="primary" fullWidth onClick={handleApply}>
          Apply
        </Button>
      </Box>
    </Box>
  );
};

export default PropertiesPanel;







